import { createLazyComponent, ChunkLoadingMetrics } from '../shared/utils/LazyLoader.js';

/**
 * APB-100: Lazy-loaded route components for code splitting optimization
 * This file contains all lazy-loaded components to improve initial bundle size
 */

// Track chunk loading performance
const trackChunkLoad = (chunkName: string) => {
  const startTime = performance.now();
  return {
    onLoad: (success: boolean) => {
      const loadTime = performance.now() - startTime;
      ChunkLoadingMetrics.recordChunkLoad(chunkName, loadTime, success);
    }
  };
};

// Authentication Components
export const LazyLoginPage = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('LoginPage');
    try {
      const module = await import('./ConditionalLoginPage.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyLogoutPage = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('LogoutPage');
    try {
      const module = await import('../features/auth/LogoutPage.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyUnauthorizedPage = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('UnauthorizedPage');
    try {
      const module = await import('../features/auth/UnauthorizedPage.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Dashboard Components
export const LazyAnalystDashboard = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('AnalystDashboard');
    try {
      const module = await import('../components/analyst/AnalystDashboard.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyExecutiveDashboard = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ExecutiveDashboard');
    try {
      const module = await import('../features/executive-dashboard/index.js');
      tracker.onLoad(true);
      // Handle named export by wrapping in default export
      return { default: (module as any).ExecutiveDashboard || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyExecutiveDashboardV2 = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ExecutiveDashboardV2');
    try {
      const module = await import('../features/executive-dashboard-v2/index.js');
      tracker.onLoad(true);
      return { default: (module as any).ExecutiveDashboardV2 || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyAutonomousPerformanceDashboard = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('AutonomousPerformanceDashboard');
    try {
      const module = await import('../components/executive/AutonomousPerformanceDashboard.js');
      tracker.onLoad(true);
      // Handle named export by wrapping in default export
      return { default: (module as any).AutonomousPerformanceDashboard || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Feature Route Components
export const LazyIntegrationHubRoutes = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('IntegrationHubRoutes');
    try {
      const module = await import('../features/integration-hub/index.js');
      tracker.onLoad(true);
      // Handle named export by wrapping in default export
      return { default: (module as any).IntegrationHubRoutes || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyMachineLearningRoutes = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('MachineLearningRoutes');
    try {
      const module = await import('../routes/MachineLearningRoutes.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyAPIManagementRoutes = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('APIManagementRoutes');
    try {
      const module = await import('../features/api-management/index.js');
      tracker.onLoad(true);
      // Handle named export by wrapping in default export
      return { default: (module as any).APIManagementRoutes || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyAdminRoutes = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('AdminRoutes');
    try {
      const module = await import('../features/admin/index.js');
      tracker.onLoad(true);
      // Handle named export by wrapping in default export
      return { default: (module as any).AdminRoutes || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazySupportRoutes = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('SupportRoutes');
    try {
      const module = await import('../features/support/index.js');
      tracker.onLoad(true);
      // Handle named export by wrapping in default export
      return { default: (module as any).SupportRoutes || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyAdvancedRoutes = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('AdvancedRoutes');
    try {
      const module = await import('../features/advanced/routes.js');
      tracker.onLoad(true);
      // Handle named export by wrapping in default export
      return { default: (module as any).AdvancedRoutes || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyInvestigationRoutes = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('InvestigationRoutes');
    try {
      const module = await import('../features/investigation/routes.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyWorkQueue = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('WorkQueue');
    try {
      const module = await import('../features/tickets/WorkQueue.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Specialized Feature Components
export const LazyThreatIntelligenceExplorer = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ThreatIntelligenceExplorer');
    try {
      const module = await import('../features/threat-intel/ThreatIntelligenceExplorer.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyUserBehaviorDashboard = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('UserBehaviorDashboard');
    try {
      const module = await import('../features/user-behavior/UserBehaviorDashboard.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyComplianceIntelligenceDashboard = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ComplianceIntelligenceDashboard');
    try {
      const module = await import('../features/compliance/ComplianceIntelligenceDashboard.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyCompliancePageTabs = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('CompliancePageTabs');
    try {
      const module = await import('../features/compliance/CompliancePageTabs.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Sprint 3 D5-rev: CMDB Asset Tagging page (/compliance/asset-tagging)
export const LazyComplianceAssetTaggingPage = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ComplianceAssetTaggingPage');
    try {
      const module = await import('../features/compliance/ComplianceAssetTaggingPage.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Sprint 3 D9: External Auditor Management page (/compliance/auditors)
export const LazyComplianceAuditorsPage = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ComplianceAuditorsPage');
    try {
      const module = await import('../features/compliance/ComplianceAuditorsPage.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Sprint 3 D7: Compliance Reports scheduling page (/compliance/reports)
export const LazyComplianceReportsPage = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ComplianceReportsPage');
    try {
      const module = await import('../features/compliance/ComplianceReportsPage.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Sprint 3 D8: Control Mapping page (/compliance/controls)
export const LazyComplianceControlsPage = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ComplianceControlsPage');
    try {
      const module = await import('../features/compliance/ComplianceControlsPage.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Sprint 3 D6: Audit Programs page (/compliance/audits)
export const LazyComplianceAuditsPage = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ComplianceAuditsPage');
    try {
      const module = await import('../features/compliance/ComplianceAuditsPage.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyComplianceStatusDashboard = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ComplianceStatusDashboard');
    try {
      const module = await import('../features/compliance/ComplianceStatusDashboard.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyAuditManagementInterface = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('AuditManagementInterface');
    try {
      const module = await import('../features/compliance/AuditManagementInterface.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyPlatformAuditLog = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('PlatformAuditLog');
    try {
      const module = await import('../features/compliance/PlatformAuditLog.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Graph Components
export const LazyGraphCanvas = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('GraphCanvas');
    try {
      const module = await import('../features/graph/GraphCanvas.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyEntityDetailPanel = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('EntityDetailPanel');
    try {
      const module = await import('../features/graph/EntityDetailPanel.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyGraphExplorer = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('GraphExplorer');
    try {
      const module = await import('../features/graph/GraphExplorer.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyGraphQueryInterface = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('GraphQueryInterface');
    try {
      const module = await import('../components/graph/GraphQueryInterface.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// DISABLED: Rules Components - Legacy SIEM paradigm (see MASTER-PRIORITY-LIST.md - OPT-06)
// Future: Transform to Policy-as-Code for autonomous guardrails
// export const LazyVisualRuleBuilder = createLazyComponent(
//   async () => {
//     const tracker = trackChunkLoad('VisualRuleBuilder');
//     try {
//       const module = await import('../features/rules/VisualRuleBuilder.js');
//       tracker.onLoad(true);
//       return module;
//     } catch (error) {
//       tracker.onLoad(false);
//       throw error;
//     }
//   }
// );

// export const LazyRuleManagementDashboard = createLazyComponent(
//   async () => {
//     const tracker = trackChunkLoad('RuleManagementDashboard');
//     try {
//       const module = await import('../features/rules/RuleManagementDashboard.js');
//       tracker.onLoad(true);
//       return module;
//     } catch (error) {
//       tracker.onLoad(false);
//       throw error;
//     }
//   }
// );

// ML Components
export const LazyMLModelRegistry = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('MLModelRegistry');
    try {
      const module = await import('../components/ml/MLModelRegistry.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyMLModelRegistryRedirect = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('MLModelRegistryRedirect');
    try {
      const module = await import('../routes/MLModelRegistryRedirect.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Threat Intelligence Components
export const LazyThreatActorProfiles = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ThreatActorProfiles');
    try {
      const module = await import('../features/threat-intel/ThreatActorProfiles.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

export const LazyThreatHuntingWorkbench = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ThreatHuntingWorkbench');
    try {
      const module = await import('../features/threat-intel/ThreatHuntingWorkbench.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Decision Policy Components - APB-1770: Policy list + CRUD
export const LazyDecisionPolicyList = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('DecisionPolicyList');
    try {
      const module = await import('../features/decision-policies/index.js');
      tracker.onLoad(true);
      return { default: (module as any).PolicyList || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Playbook Components - B7: Playbooks & Automation
export const LazyPlaybookSuggestionQueue = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('PlaybookSuggestionQueue');
    try {
      const module = await import('../features/playbooks/components/PlaybookSuggestionQueue.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// EPIC-007: Shadow AI Discovery
export const LazyShadowAIDiscoveryRoutes = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ShadowAIDiscovery');
    try {
      const module = await import('../features/shadow-ai-discovery/routes.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// APB-1964: Agent Registry
export const LazyAgentRegistry = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('AgentRegistry');
    try {
      const module = await import('../features/agent-registry/index.js');
      tracker.onLoad(true);
      return { default: (module as any).AgentRegistryPage || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// APB-1964: Agent Detail Page
export const LazyAgentDetail = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('AgentDetail');
    try {
      const module = await import('../features/agent-registry/pages/AgentDetailPage.js');
      tracker.onLoad(true);
      return { default: (module as any).AgentDetailPage || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Regulatory Notifications (lazy loaded) - APB-1974: AI Governance Notifications
export const LazyRegulatoryNotifications = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('RegulatoryNotifications');
    try {
      const module = await import('../features/ai-governance/pages/RegulatoryNotificationPage.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Threat View (Exposure) — Pentest operations platform
export const LazyThreatViewRoutes = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ThreatViewRoutes');
    try {
      const module = await import('../features/threat-view/routes/ThreatViewRoutes');
      tracker.onLoad(true);
      return { default: (module as any).ThreatViewRoutes || (module as any).default || module };
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Connector Health Dashboard - Diagnostic UI for connector troubleshooting
export const LazyConnectorHealthDashboard = createLazyComponent(
  async () => {
    const tracker = trackChunkLoad('ConnectorHealthDashboard');
    try {
      const module = await import('../features/connectors/ConnectorHealthDashboard.js');
      tracker.onLoad(true);
      return module;
    } catch (error) {
      tracker.onLoad(false);
      throw error;
    }
  }
);

// Layout Components (keep these eager for better UX)
export { MainLayout } from '../components/layout/MainLayout';
export { TenantGuard } from '../components/TenantGuard';
export { RouteGuard } from '../shared/guards/RouteGuard';
